// Base service class - provides shared HTTP request functionality for all services
import csrfService from '../../services/csrfService.js';
import { authErrorManager } from './AuthErrorManager.js';
import { frontendLogger } from '../../services/frontendLogger.js';
import { urlConfig } from '../../config/urlConfig.js';

class BaseService {
  constructor() {
    this.baseURL = `${urlConfig.api.baseURL}/api`;
  }

  // Get auth token from localStorage (supports both admin and regular tokens)
  getAuthToken() {
    return localStorage.getItem('sanctuary_token') || localStorage.getItem('authToken') || localStorage.getItem('token');
  }

  // Alias for backward compatibility
  getToken() {
    return this.getAuthToken();
  }

  // Get stored user data
  getUser() {
    const userStr = localStorage.getItem('sanctuary_user');
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  // Store user data
  setUser(user) {
    localStorage.setItem('sanctuary_user', JSON.stringify(user));
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getAuthToken();
    const user = this.getUser();
    return !!(token && user);
  }

  // Get user's current tier
  getCurrentTier() {
    const user = this.getUser();
    return user?.current_tier || 'seeker';
  }

  // Store token
  setToken(token) {
    localStorage.setItem('sanctuary_token', token);
  }

  // Remove authentication data
  removeToken() {
    localStorage.removeItem('sanctuary_token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('sanctuary_user');
  }

  // Create request headers with auth
  getHeaders() {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Transform technical errors into user-friendly messages
  getUserFriendlyError(error) {
    const message = error.message || error.toString();
    
    // Authentication errors
    if (message.includes('Missing Bearer token') || message.includes('Invalid token') || message.includes('Unauthorized')) {
      return 'Session expired. Please log in again.';
    }
    // Network errors
    if (message.includes('Failed to fetch') || message.includes('Network')) {
      return 'Connection error. Please check your internet and try again.';
    }
    // Server errors
    if (message.includes('HTTP 500') || message.includes('Internal Server Error')) {
      return 'Server error. Please try again in a moment.';
    }
    // Not found errors
    if (message.includes('HTTP 404') || message.includes('Not Found')) {
      return 'The requested item was not found.';
    }
    // Forbidden errors
    if (message.includes('HTTP 403') || message.includes('Forbidden')) {
      return 'You do not have permission to perform this action.';
    }
    // Default fallback for unknown errors
    return 'Something went wrong. Please try again.';
  }

  // Generic request method
  async request(endpoint, options = {}) {
    // For POST and PATCH requests, use CSRF service
    if (options.method === 'POST') {
      // If body is already a string, parse it; if it's an object, use it directly
      const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body || {};
      
      try {
        return await csrfService.post(endpoint, body);
      } catch (error) {
        frontendLogger.error('API Request Error', {
          endpoint,
          error: error.message,
          stack: error.stack,
          method: 'POST',
          body: body
        }, 'BaseService');
        
        // For login endpoint, preserve original error message
        if (endpoint === '/auth/signin') {
          throw new Error(error.message || 'Invalid credentials');
        }
        
        // For other endpoints, clear auth data on 401
        if (error.message?.includes('Session expired')) {
          this.removeToken();
        }
        
        // Transform technical error into user-friendly message
        const friendlyError = new Error(this.getUserFriendlyError(error));
        throw friendlyError;
      }
    }

    if (options.method === 'PATCH') {
      // If body is already a string, parse it; if it's an object, use it directly
      const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body || {};
      
      try {
        return await csrfService.patch(endpoint, body);
      } catch (error) {
        frontendLogger.error('API Request Error', {
          endpoint,
          error: error.message,
          stack: error.stack,
          method: 'PATCH',
          body: body
        }, 'BaseService');
        
        // For other endpoints, clear auth data on 401
        if (error.message?.includes('Session expired')) {
          this.removeToken();
        }
        
        // Transform technical error into user-friendly message
        const friendlyError = new Error(this.getUserFriendlyError(error));
        throw friendlyError;
      }
    }

    // For GET requests, use regular fetch
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      headers: this.getHeaders(),
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      // Handle 401 Unauthorized - try retries first, then use progressive error handling
      if (response.status === 401) {
        // Only record auth error if this is our final attempt
        // This prevents network issues from being counted as auth failures
        if (options.isRetryAttempt) {
          authErrorManager.recordAuthError(endpoint);
          this.removeToken();
          throw new Error('Session expired. Please log in again.');
        }
        
        // For first 401, don't record as auth error yet - might be temporary
        this.removeToken();
        throw new Error('Session expired. Please log in again.');
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      frontendLogger.error('API Request Error', {
        endpoint,
        error: error.message,
        stack: error.stack,
        method: options.method || 'GET',
        authenticated: true
      }, 'BaseService');
      // Transform technical error into user-friendly message
      const friendlyError = new Error(this.getUserFriendlyError(error));
      throw friendlyError;
    }
  }

  // Public request method (for non-authenticated endpoints)
  async publicRequest(endpoint, options = {}) {
    // For POST and PATCH requests, use CSRF service even for public endpoints
    if (options.method === 'POST') {
      // If body is already a string, parse it; if it's an object, use it directly
      const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body || {};
      return await csrfService.post(endpoint, body);
    }

    if (options.method === 'PATCH') {
      // If body is already a string, parse it; if it's an object, use it directly
      const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body || {};
      return await csrfService.patch(endpoint, body);
    }

    // For GET requests, use regular fetch
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      // For public endpoints, don't handle 401 with redirects
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      frontendLogger.error('Public API Request Error', {
        endpoint,
        error: error.message,
        stack: error.stack,
        method: options.method || 'GET',
        authenticated: false
      }, 'BaseService');
      // Transform technical error into user-friendly message
      const friendlyError = new Error(this.getUserFriendlyError(error));
      throw friendlyError;
    }
  }

  // Get subscription status
  async getSubscription() {
    const response = await this.request('/subscriptions/me');
    return response;
  }

  // Get current user profile
  async getProfile() {
    const response = await this.request('/auth/me');
    
    if (response.user && response.profile) {
      // Merge auth user with profile data
      const userData = {
        id: response.user.id,
        email: response.user.email,
        display_name: response.profile.display_name,
        current_tier: response.profile.current_tier || 'seeker',
        onboarding_answers: response.profile.onboarding_answers,
        preferences: response.profile.preferences,
        active_archetype: response.profile.active_archetype,
        created_at: response.profile.created_at,
        avatar_url: response.profile.avatar_url,
        is_active: response.profile.is_active,
        last_login_at: response.profile.last_login_at
      };
      
      this.setUser(userData);
      return userData;
    }

    return response;
  }

  // File upload request method with CSRF protection
  async requestWithFile(endpoint, options = {}) {
    try {
      // Get CSRF token
      const csrfToken = await csrfService.getCSRFToken();
      
      // Get Bearer token for authentication
      const bearerToken = this.getAuthToken();
      
      // Prepare headers (no Content-Type for FormData - browser sets it)
      const headers = {
        'X-CSRF-Token': csrfToken,
        ...(bearerToken && { 'Authorization': `Bearer ${bearerToken}` })
      };

      const requestOptions = {
        method: options.method || 'POST',
        headers,
        credentials: 'include',
        body: options.body // FormData
      };

      const response = await fetch(`${this.baseURL}${endpoint}`, requestOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      frontendLogger.error('File Upload Error', {
        endpoint,
        error: error.message,
        stack: error.stack
      }, 'BaseService');
      
      throw error;
    }
  }

  // Check onboarding status
  hasCompletedOnboarding() {
    const user = this.getUser();
    return user?.preferences?.completed_onboarding || false;
  }

  // Update user profile
  async updateProfile(updates) {
    const response = await this.request('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });

    if (response.profile) {
      // Update stored user data
      const currentUser = this.getUser();
      const updatedUser = {
        ...currentUser,
        ...response.profile
      };
      this.setUser(updatedUser);
    }

    return response;
  }

}

export { BaseService };
export default BaseService;